import { defineComponent as _defineComponent } from 'vue'
import { computed, ref } from 'vue';
import { ArticleV1, ArticleContentMetaV2, type Publisher } from '@ilteducation/content-registry-types';
import { defaultRealms, Realm } from '@/foundations/helpers/defaultRealms';
import { generateExercise } from '@/data-provider/aiService';
import eventBus from '@/foundations/services/eventBus';
import ArticleAudioImportButton from '@/components/article/ArticleAudioImportButton.vue';
import DownloadArticleStats from '@/components/article/DownloadArticleStats.vue';
import ConfirmationDialog from '@/components/ConfirmationDialog.vue';
import axios from 'axios';
import { getAvailablePublishers } from '@/data-provider/refDataProvider';

type Props = {
  title: string;
  resource: unknown;
};

export default /*#__PURE__*/_defineComponent({
  __name: 'List',
  props: {
    title: null,
    resource: null
  },
  setup(__props: any) {

const props = __props as {
  title: string;
  resource: unknown;
}



const availablePublishers = ref<Publisher[]>([]);
getAvailablePublishers().then((publishers: Publisher[]) => {
  availablePublishers.value = publishers.sort((a: Publisher, b: Publisher) => a.name.localeCompare(b.name));
});

const pendingImages = ref<Record<string, boolean>>({});

const fields = [
  { source: 'cover' },
  {
    source: 'id',
    link: 'edit',
    sortable: true,
  },
  {
    source: 'headline',
    sortable: true,
  },
  {
    source: 'author',
    sortable: true,
  },
  {
    source: 'publisher',
    sortable: true,
  },
  {
    source: 'mediaSource',
    type: 'array',
    sortable: true,
  },
  {
    source: 'content.wordCount',
    label: 'Words',
    sortable: true,
  },
  {
    source: 'isPublished',
    sortable: true,
    type: 'boolean',
  },
  {
    source: 'properties.additionalContent',
    sortable: true,
    label: 'Has Exercises',
    type: 'boolean',
  },
  {
    source: 'lastUpdatedAt',
    sortable: true,
  },
  {
    source: 'realm',
    label: 'Market',
    sortable: true,
    type: 'chip',
    attributes: {
      color: 'orange',
    },
  },
  {
    source: 'audio',
    label: 'Audio',
    sortable: false,
  },
];

const filters = computed(() => [
  {
    source: 'author',
    type: 'text',
    attributes: {
      multiple: true,
    },
  },
  {
    source: 'publisher',
    label: 'Publisher',
    type: 'autocomplete',
    attributes: {
      multiple: true,
      choices: availablePublishers.value,
      itemText: (item: Publisher) => item.name,
      minChars: 1000,
    },
  },
  {
    source: 'mediaSource',
    type: 'text',
    attributes: {
      multiple: true,
    },
  },
  {
    source: 'state',
    type: 'select',
    attributes: {
      multiple: true,
    },
  },
  {
    source: 'isPublished',
    type: 'boolean',
    multiple: false,
  },
  {
    label: 'Language',
    source: 'content.language',
    type: 'select',
    attributes: {
      choices: ['en', 'sv'],
      optionText: 'language',
      multiple: true,
    },
  },
  {
    source: 'properties.additionalContent',
    type: 'boolean',
    label: 'Has Exercises',
    multiple: false,
  },
  {
    source: 'realm',
    label: 'Market',
    type: 'select',
    attributes: {
      multiple: true,
      choices: defaultRealms,
      itemText: (item: Realm) => item.realm,
      itemValue: (item: Realm) => item.code,
    },
  },
]);

const isArticleContentMetaV2 = (val: any): val is ArticleContentMetaV2 => val !== undefined && val.path !== undefined;

const generateTS = async (items: ArticleV1[]) => {
  console.log('Triggering Audio creation job');

  await Promise.all(
    items.map((item) => {
      axios.post(`${process.env.VUE_APP_CONTENT_API_URL}${item.id}/textMarking`);
    }),
  );
  eventBus.$emit('snackbar.open', `Generation of audio started for ${items.length} articles`);
};

const generateExercises = async (items: ArticleV1[]) => {
  await Promise.all(
    items.flatMap((item) => {
      const language = item.publisher?.includes('BBC') ? 'en' : 'swe';

      if (!isArticleContentMetaV2(item?.contentMeta)) {
        console.log('No path for article', item.id);
        return [];
      }

      return [
        generateExercise(item.id, 'short_answer', item.realm, language, item.contentMeta.path),
        generateExercise(item.id, 'discussion', item.realm, language, item.contentMeta.path),
      ];
    }),
  );
  eventBus.$emit('snackbar.open', `Generation of AI questions started for ${items.length} articles`);
};

const publishOrUnpublishBulk = async (items: ArticleV1[], action: 'publish' | 'unpublish') => {
  await Promise.all(
    items.map((item) => {
      const { state, createdAt, lastUpdatedAt, id, ...rest } = item;
      const updatedItem = { ...rest, isPublished: action === 'publish' };
      return axios.put(`${process.env.VUE_APP_CONTENT_API_URL}${item.id}`, updatedItem);
    }),
  );
  eventBus.$emit('snackbar.open', `${action === 'publish' ? 'P' : 'Unp'}ublished ${items.length} articles`);
};

const hasSyntheticAudio = (item: ArticleV1): boolean => {
  if (!item.audio) {
    return false;
  }
  return Object.values(item.audio).some((audioDef: any) => audioDef.sourceType === 'synthetic');
};

const hasAudio = (item: ArticleV1): boolean => {
  return !!(item.audio && Object.keys(item.audio).length > 0);
};

const confirmDialog = ref<InstanceType<typeof ConfirmationDialog> | null>(null);

const confirmAndGenerateTS = async (items: ArticleV1[]) => {
  const anyHaveAudio = items.some(hasAudio);

  if (anyHaveAudio) {
    const confirmed = await confirmDialog.value?.open(
      'Confirm Audio Generation',
      'One or more of the selected articles already have audio. Are you sure you want to regenerate?',
      'No',
      'Yes',
    );
    if (!confirmed) return;
  }
  await generateTS(items);
};

return { __sfc: true,props, availablePublishers, pendingImages, fields, filters, isArticleContentMetaV2, generateTS, generateExercises, publishOrUnpublishBulk, hasSyntheticAudio, hasAudio, confirmDialog, confirmAndGenerateTS, ArticleAudioImportButton, DownloadArticleStats, ConfirmationDialog }
}

})